import { Landmark, TrendingUp, Smartphone, CreditCard, PiggyBank, Bitcoin, Layers, Brain, Cloud } from "lucide-react";

export const industries = [
  {
    slug: "fintech-banking",
    icon: Landmark,
    title: "FinTech & Banking",
    shortDesc: "Core banking systems, neobank platforms, lending infrastructure, and regulatory compliance engines.",
    heroSubtitle: "We build the technology that powers modern financial institutions. From core banking to compliance automation, our systems process millions of transactions daily.",
    stats: ["$50M+ daily volume processed", "Zero regulatory penalties", "5 banking cores modernized"],
    challenges: [
      "Legacy infrastructure that costs millions to maintain and can't support modern digital experiences",
      "Regulatory complexity across multiple jurisdictions with constantly evolving requirements",
      "Customer expectations shaped by consumer apps while operating under banking-grade security standards"
    ],
    solutions: [
      "Modern core banking platforms built on microservices with event-driven architecture",
      "Compliance-as-Code engines that auto-adapt to regulatory changes across jurisdictions",
      "Digital-first customer experiences with banking-grade security and sub-second response times"
    ],
    capabilities: [
      { title: "Core Banking Modernization", desc: "Migrate legacy systems to modern microservices while maintaining zero downtime" },
      { title: "Open Banking (PSD2/PSD3)", desc: "API-first banking platforms with third-party connectivity and consent management" },
      { title: "KYC/AML Automation", desc: "Real-time identity verification and transaction monitoring with ML scoring" },
      { title: "Lending Platforms", desc: "Automated underwriting, credit scoring, and loan management systems" },
      { title: "Regulatory Reporting", desc: "Automated SAR, CTR, and jurisdiction-specific regulatory report generation" },
      { title: "Digital Account Opening", desc: "Frictionless onboarding with identity verification and instant account provisioning" }
    ],
    relatedCaseStudies: ["ai-fintech-assistant", "legacy-banking-modernization", "compliance-as-code"],
    techStack: ["Python", "Go", "PostgreSQL", "Kafka", "Redis", "AWS", "Terraform", "Vault"],
  },
  {
    slug: "trading-capital-markets",
    icon: TrendingUp,
    title: "Trading & Capital Markets",
    shortDesc: "Algorithmic execution engines, smart order routing, market data systems, and risk management platforms.",
    heroSubtitle: "Institutional-grade trading infrastructure for prop firms, hedge funds, and brokerages. Sub-millisecond latency. Real-time risk controls. Multi-venue execution.",
    stats: ["<8ms avg execution latency", "$2M+ daily trading volume", "99.99% system uptime"],
    challenges: [
      "Competing against firms with dedicated infrastructure teams and millions in technology budgets",
      "Latency measured in microseconds where every delay translates directly to financial losses",
      "Multi-venue complexity with different protocols, order types, and regulatory requirements per exchange"
    ],
    solutions: [
      "High-performance execution engines in Rust with lock-free data structures and kernel bypass networking",
      "Smart order routing that optimizes across venues based on liquidity, fees, and execution probability",
      "Real-time risk management with pre-trade checks, position limits, and automatic circuit breakers"
    ],
    capabilities: [
      { title: "Order Management Systems", desc: "Multi-venue OMS with smart routing, partial fills, and complete lifecycle tracking" },
      { title: "Market Data Infrastructure", desc: "Normalized feeds from exchanges and vendors with sub-millisecond distribution" },
      { title: "Execution Algorithms", desc: "TWAP, VWAP, implementation shortfall, and custom execution strategies" },
      { title: "Risk Management Engines", desc: "Pre-trade and post-trade risk checks with configurable limits and circuit breakers" },
      { title: "Backtesting Frameworks", desc: "Tick-level historical simulation with realistic slippage and market impact models" },
      { title: "FIX Protocol Integration", desc: "FIX 4.2/4.4/5.0 connectivity to exchanges, prime brokers, and ECNs" }
    ],
    relatedCaseStudies: ["algorithmic-trading-platform", "trading-risk-management"],
    techStack: ["Rust", "C++", "Python", "FIX Protocol", "WebSocket", "TimescaleDB", "Grafana", "Kubernetes"],
  },
  {
    slug: "digital-banking",
    icon: Smartphone,
    title: "Digital Banking",
    shortDesc: "Neobank platforms, mobile banking apps, digital wallets, and customer engagement systems.",
    heroSubtitle: "Build digital banking experiences that customers actually want to use. Mobile-first, real-time, and built for the expectations of 2026.",
    stats: ["500K+ end users supported", "4.8 avg app store rating", "12min avg session duration"],
    challenges: [
      "Traditional banks struggling to compete with neobanks that ship features weekly",
      "Customer churn driven by poor mobile experiences and slow feature delivery",
      "Integration complexity with legacy core banking systems that weren't designed for real-time APIs"
    ],
    solutions: [
      "Mobile-first banking apps with offline support, biometric auth, and real-time notifications",
      "API gateway layers that bridge modern frontends to legacy core banking systems",
      "Continuous delivery pipelines that enable weekly releases without sacrificing stability"
    ],
    capabilities: [
      { title: "Mobile Banking Apps", desc: "Native and cross-platform apps with biometric auth, real-time notifications, and offline support" },
      { title: "Digital Wallets", desc: "Multi-currency wallets with P2P transfers, bill payments, and card management" },
      { title: "Personal Finance Tools", desc: "Spending analytics, budgeting, savings goals, and financial health scoring" },
      { title: "Customer Onboarding", desc: "Frictionless digital onboarding with eKYC, video verification, and instant activation" },
      { title: "Notification Systems", desc: "Real-time alerts for transactions, security events, and personalized financial insights" },
      { title: "Card Management", desc: "Virtual and physical card issuing, spending controls, and reward programs" }
    ],
    relatedCaseStudies: ["legacy-banking-modernization", "ai-fintech-assistant"],
    techStack: ["React Native", "Swift", "Kotlin", "Node.js", "PostgreSQL", "Redis", "Firebase", "AWS"],
  },
  {
    slug: "payments",
    icon: CreditCard,
    title: "Payments",
    shortDesc: "Payment orchestration, gateway integration, reconciliation engines, and cross-border settlement.",
    heroSubtitle: "Payment infrastructure that processes millions of transactions with automatic failover, smart routing, and real-time reconciliation.",
    stats: ["$50M+ daily volume", "99.99% processing uptime", "200ms avg transaction time"],
    challenges: [
      "Payment failures costing businesses 5-10% of revenue through declined transactions and poor retry logic",
      "Multi-provider complexity with different APIs, settlement timelines, and fee structures per region",
      "Reconciliation nightmares when transaction records don't match across providers, banks, and internal systems"
    ],
    solutions: [
      "Payment orchestration layers that route transactions to the optimal provider based on cost, speed, and success rate",
      "Automatic retry and failover logic that recovers from provider outages without customer impact",
      "Real-time reconciliation engines that match transactions across all systems within minutes, not days"
    ],
    capabilities: [
      { title: "Payment Orchestration", desc: "Multi-provider routing with intelligent failover and cost optimization across PSPs" },
      { title: "Gateway Integration", desc: "Pre-built connectors for Stripe, Adyen, PayPal, Razorpay, and 50+ regional providers" },
      { title: "Reconciliation Engines", desc: "Automated matching across providers, banks, and internal ledgers with exception handling" },
      { title: "Cross-Border Payments", desc: "Multi-currency processing with FX optimization and local payment method support" },
      { title: "Subscription Billing", desc: "Recurring billing with dunning management, proration, and usage-based metering" },
      { title: "Fraud Prevention", desc: "Real-time transaction scoring with ML models trained on financial fraud patterns" }
    ],
    relatedCaseStudies: ["payment-gateway-platform", "compliance-as-code"],
    techStack: ["Go", "Python", "PostgreSQL", "Kafka", "Redis", "Stripe", "AWS Lambda", "Terraform"],
  },
  {
    slug: "wealth-management",
    icon: PiggyBank,
    title: "Wealth Management",
    shortDesc: "Robo-advisory platforms, portfolio management systems, and investment analytics dashboards.",
    heroSubtitle: "Technology platforms for wealth managers, RIAs, and robo-advisors. Automated portfolio construction, rebalancing, and client reporting.",
    stats: ["$100M+ AUM managed", "Automated rebalancing", "Real-time portfolio analytics"],
    challenges: [
      "Manual portfolio management processes that don't scale beyond a few hundred client accounts",
      "Client expectations for real-time portfolio visibility and self-service features",
      "Regulatory reporting requirements that consume disproportionate advisor time"
    ],
    solutions: [
      "Automated portfolio construction and rebalancing engines with tax-loss harvesting",
      "Client-facing portals with real-time portfolio tracking, document access, and secure messaging",
      "Automated compliance reporting and audit trail generation for regulatory requirements"
    ],
    capabilities: [
      { title: "Robo-Advisory Platforms", desc: "Automated portfolio construction based on risk profiles, goals, and investment horizons" },
      { title: "Portfolio Rebalancing", desc: "Tax-aware rebalancing with drift detection, threshold triggers, and batch execution" },
      { title: "Client Portals", desc: "Real-time portfolio dashboards, document vault, secure messaging, and e-signatures" },
      { title: "Investment Analytics", desc: "Performance attribution, risk metrics, benchmark comparison, and custom reporting" },
      { title: "Model Portfolio Management", desc: "Centralized model creation, assignment, and bulk rebalancing across accounts" },
      { title: "Regulatory Compliance", desc: "Automated ADV filing, best execution reporting, and client suitability documentation" }
    ],
    relatedCaseStudies: ["robo-advisor-platform", "ai-fintech-assistant"],
    techStack: ["Python", "React", "PostgreSQL", "Redis", "FastAPI", "Celery", "AWS", "Docker"],
  },
  {
    slug: "crypto-web3",
    icon: Bitcoin,
    title: "Crypto & Web3",
    shortDesc: "DeFi protocols, NFT platforms, exchange infrastructure, and wallet systems.",
    heroSubtitle: "Secure, gas-optimized blockchain applications. From smart contracts to full exchange platforms, built with security as the foundation.",
    stats: ["0 audit vulnerabilities", "$10M+ TVL managed", "40% lower gas costs"],
    challenges: [
      "Smart contract vulnerabilities that can drain user funds with no way to reverse transactions",
      "Gas optimization challenges that directly impact user adoption and protocol competitiveness",
      "Rapidly evolving ecosystem with new chains, standards, and security vectors emerging monthly"
    ],
    solutions: [
      "Security-first development with formal verification, extensive testing, and professional audit preparation",
      "Gas-optimized contract patterns including batch operations, lazy minting, and efficient storage layouts",
      "Multi-chain architecture that deploys to EVM chains, Solana, and emerging L2s from a single codebase"
    ],
    capabilities: [
      { title: "Smart Contract Development", desc: "Gas-optimized Solidity and Rust contracts with formal verification and audit preparation" },
      { title: "DeFi Protocols", desc: "Custom AMMs, lending protocols, yield aggregators, and liquidity mining programs" },
      { title: "Exchange Infrastructure", desc: "Order matching engines, custody solutions, and fiat on/off ramp integration" },
      { title: "NFT Platforms", desc: "Marketplaces, minting engines, staking mechanisms, and royalty systems" },
      { title: "Wallet Systems", desc: "Multi-chain wallet connectivity, MPC custody, and account abstraction for UX" },
      { title: "Cross-Chain Bridges", desc: "Secure asset bridging with relay verification and multi-sig governance" }
    ],
    relatedCaseStudies: ["defi-nft-marketplace", "algorithmic-trading-platform"],
    techStack: ["Solidity", "Rust", "Hardhat", "Foundry", "React", "Ethers.js", "The Graph", "IPFS"],
  },
  {
    slug: "saas-platforms",
    icon: Layers,
    title: "SaaS & Platforms",
    shortDesc: "Multi-tenant SaaS products, subscription billing, admin dashboards, analytics platforms, and API-driven products.",
    heroSubtitle: "Build SaaS products designed for growth. Multi-tenant architecture, flexible billing, analytics, and admin tools that scale from 10 to 10,000 customers.",
    stats: ["Multi-tenant architecture", "Subscription billing ready", "Built for scale"],
    challenges: [
      "SaaS architecture is deceptively complex — multi-tenancy, subscription billing, role-based access, usage metering all need to work together",
      "Getting billing wrong directly impacts revenue — failed charges, broken upgrade flows, incorrect invoices",
      "Infrastructure that works at 100 users often falls apart at 10,000 without proper planning"
    ],
    solutions: [
      "Multi-tenant architecture with secure data isolation and flexible database strategies",
      "Stripe-integrated billing supporting multiple plan tiers, usage-based metering, and dunning management",
      "Real-time analytics dashboards, admin panels, and role-based access control from day one"
    ],
    capabilities: [
      { title: "Multi-Tenant Architecture", desc: "Secure data isolation with shared or dedicated database strategies depending on compliance needs" },
      { title: "Subscription Billing", desc: "Stripe integration with plan tiers, usage metering, promo codes, and dunning management" },
      { title: "User Management", desc: "Authentication, teams, invitations, role-based access, and SSO integration" },
      { title: "Analytics Dashboard", desc: "Real-time product analytics, engagement metrics, cohort analysis, and custom reporting" },
      { title: "Admin Panel", desc: "Tenant management, feature flags, content management, and support tools" },
      { title: "API Layer", desc: "RESTful or GraphQL APIs for third-party integrations and developer ecosystem" }
    ],
    relatedCaseStudies: ["multi-platform-saas", "sample-saas-mvp"],
    techStack: ["Next.js", "Node.js", "PostgreSQL", "Stripe", "Redis", "Vercel", "AWS", "Docker"],
  },
  {
    slug: "ai-automation",
    icon: Brain,
    title: "AI & Automation",
    shortDesc: "RAG systems, AI chatbots, ML pipelines, document processing, workflow automation, and intelligent assistants.",
    heroSubtitle: "Integrate production-grade AI into your products. RAG systems, custom chatbots, ML pipelines, and intelligent automation that works beyond the demo.",
    stats: ["Production-grade AI systems", "RAG, chatbots, ML pipelines", "Compliance-ready architecture"],
    challenges: [
      "The gap between an AI demo and a production system that handles real users is where most teams get stuck",
      "Off-the-shelf chatbot solutions fail to meet domain-specific or regulatory requirements",
      "AI accuracy, cost management, and latency budgets all need to be solved simultaneously"
    ],
    solutions: [
      "Custom RAG systems with domain-specific embedding pipelines and hybrid retrieval",
      "Compliance-ready AI architecture with PII stripping, audit trails, and human escalation",
      "Evaluation pipelines that continuously monitor accuracy, drift, and reliability in production"
    ],
    capabilities: [
      { title: "RAG Systems", desc: "Custom retrieval-augmented generation with your data, vector search, and contextual responses" },
      { title: "AI Chatbots", desc: "Intelligent conversational agents with domain training, escalation handling, and compliance guardrails" },
      { title: "ML Pipelines", desc: "End-to-end machine learning pipelines from data ingestion to model deployment" },
      { title: "Document Processing", desc: "Intelligent extraction, classification, and summarization for complex documents" },
      { title: "Workflow Automation", desc: "AI-powered automation for repetitive tasks with human-in-the-loop oversight" },
      { title: "Evaluation & Monitoring", desc: "Continuous evaluation tracking accuracy, drift, and reliability in production" }
    ],
    relatedCaseStudies: ["ai-fintech-assistant", "compliance-as-code"],
    techStack: ["Python", "OpenAI", "Claude", "LangChain", "FastAPI", "Pinecone", "Redis", "Docker"],
  },
  {
    slug: "mobile-apps",
    icon: Smartphone,
    title: "Mobile Apps",
    shortDesc: "Cross-platform and native mobile applications with offline support, push notifications, and seamless backend integration.",
    heroSubtitle: "Build mobile experiences users actually want to use. Cross-platform or native, with real-time sync, offline support, and production-grade backends.",
    stats: ["Cross-platform & native", "Offline-first capable", "Backend included"],
    challenges: [
      "Building separate iOS and Android apps doubles development time and maintenance burden",
      "Mobile apps need robust offline support, push notifications, and seamless sync that most web teams struggle with",
      "App store approval processes, performance requirements, and platform-specific guidelines add complexity"
    ],
    solutions: [
      "React Native or Flutter for cross-platform development with near-native performance",
      "Offline-first architecture with background sync, push notifications, and real-time updates",
      "Complete backend API development alongside the mobile app for a cohesive product"
    ],
    capabilities: [
      { title: "Cross-Platform Development", desc: "React Native or Flutter apps with shared business logic and near-native performance" },
      { title: "Native iOS & Android", desc: "Swift and Kotlin development for platform-specific features and maximum performance" },
      { title: "Offline-First Architecture", desc: "Local data storage, background sync, and conflict resolution for unreliable connectivity" },
      { title: "Push Notifications", desc: "Targeted notifications, in-app messaging, and engagement-driving communication" },
      { title: "Backend APIs", desc: "Complete REST or GraphQL backend with authentication, real-time sync, and admin tools" },
      { title: "App Store Deployment", desc: "Build optimization, store listing preparation, and CI/CD for mobile releases" }
    ],
    relatedCaseStudies: ["multi-platform-saas", "sample-saas-mvp"],
    techStack: ["React Native", "Flutter", "Swift", "Kotlin", "Node.js", "PostgreSQL", "Firebase", "AWS"],
  },
  {
    slug: "enterprise",
    icon: Cloud,
    title: "Enterprise",
    shortDesc: "Legacy modernization, microservices migration, cloud infrastructure, API platforms, and enterprise integration.",
    heroSubtitle: "Modernize legacy systems without disrupting operations. Microservices migration, cloud infrastructure, and API platforms built for enterprise scale.",
    stats: ["Zero-downtime migration", "Infrastructure as code", "Enterprise-grade security"],
    challenges: [
      "Legacy monoliths with zero documentation and departed original developers make changes risky and slow",
      "Cloud costs spiral without proper architecture — many enterprises pay 3-5x what they should",
      "Enterprise integration complexity with multiple systems, protocols, and data formats"
    ],
    solutions: [
      "Strangler fig pattern for incremental migration from monoliths to microservices with zero downtime",
      "Cost-optimized cloud architecture with infrastructure as code and automated scaling",
      "API gateway layers that bridge modern services to legacy systems"
    ],
    capabilities: [
      { title: "Legacy Modernization", desc: "Migrate monoliths to microservices incrementally while maintaining zero downtime" },
      { title: "Cloud Architecture", desc: "AWS/GCP design with high availability, disaster recovery, and multi-region support" },
      { title: "API Platform Development", desc: "Internal and external API platforms with documentation, SDKs, and developer portals" },
      { title: "DevOps & CI/CD", desc: "Automated build, test, and deployment pipelines with progressive rollouts" },
      { title: "Data Migration", desc: "Database migration with validation, rollback strategies, and zero data loss" },
      { title: "Monitoring & Observability", desc: "Comprehensive metrics, logs, traces, and intelligent alerting" }
    ],
    relatedCaseStudies: ["legacy-banking-modernization", "compliance-as-code"],
    techStack: ["Java", "Spring Boot", "Python", "Go", "PostgreSQL", "Kafka", "Kubernetes", "Terraform"],
  },
];

